import { PropsWithChildren, useEffect, useMemo } from 'react';
import {
    CssBaseline,
    ThemeProvider as MuiThemeProvider,
    createTheme,
    useMediaQuery
} from '@mui/material';
import { useColorSchemeOptional } from '../contexts/color-scheme-context';
import { useCSSVariable } from '../hooks/use-css-variable';
import { applyColorSchemeBodyClass } from '../util/color-scheme-preference';

export default function ThemeProvider({ children }: PropsWithChildren) {
    const colorScheme = useColorSchemeOptional();
    const darkEnabled = useCSSVariable(
        document.getElementsByTagName('head')[0],
        document.getElementsByTagName('body')[0],
        '--dark-mode-enabled'
    );
    const darkOverride = useCSSVariable(
        document.getElementsByTagName('body')[0],
        document.getElementsByTagName('body')[0],
        '--dark-mode-override'
    );
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const useDarkFromBrand =
        darkEnabled === '1' &&
        ((darkOverride !== '0' && prefersDarkMode) || darkOverride === '1');
    const useDark = colorScheme?.isDark ?? useDarkFromBrand;

    useEffect(() => {
        if (colorScheme) {
            return;
        }
        applyColorSchemeBodyClass(useDark);
    }, [colorScheme, useDark]);

    const appTheme = useMemo(
        () =>
            createTheme({
                cssVariables: true,
                palette: {
                    mode: useDark ? 'dark' : 'light',
                    action: {
                        hover: 'rgba(0, 0, 0, 0.03)'
                    }
                },
                // Admin + AI chat side panels use 1300–1301. Keep modals (dialogs) above them.
                zIndex: {
                    modal: 1400,
                    snackbar: 1500,
                    tooltip: 1600
                },
                components: {
                    MuiAccordion: {
                        styleOverrides: {
                            root: {
                                borderRadius: 'var(--container-radius, 4px)'
                            }
                        }
                    },
                    MuiAppBar: {
                        styleOverrides: {
                            root: {
                                backgroundColor: 'var(--background-body, #F8F8F8)',
                                color: 'var(--primary-text, rgba(0, 0, 0, 0.87));',
                                zIndex: 1200
                            }
                        }
                    },
                    MuiDrawer: {
                        styleOverrides: {
                            root: {
                                '& .MuiBackdrop-root': {
                                    backdropFilter: 'blur(0.25em)'
                                }
                            }
                        }
                    },
                    MuiBreadcrumbs: {
                        styleOverrides: {
                            root: {
                                '& .MuiBreadcrumbs-ol': {
                                    color: 'var(--text-secondary, rgba(0, 0, 0, 0.60));'
                                },
                                '& .MuiBreadcrumbs-li a': {
                                    color: 'var(--text-secondary, rgba(0, 0, 0, 0.60));',
                                    display: 'inline-block',
                                    textDecoration: 'none'
                                },
                                '& .MuiBreadcrumbs-li a:hover': {
                                    color: 'var(--primary-main, #1565C0);',
                                    borderBottom: '2px solid var(--primary-main, #1565C0);'
                                }
                            }
                        }
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                borderRadius: 'var(--button-radius, 4px)',
                                fontFamily: 'var(--button-font-family, "Inter")',
                                fontWeight: 'var(--button-weight, 400)',
                                '&.circle-button': {
                                    background: 'var(--background-content, #FFFFFF)',
                                    borderRadius: '2em',
                                    minWidth: '3em',
                                    minHeight: '3em',
                                    padding: 0
                                }
                            },
                            contained: {
                                color: 'var(--button-color)',
                                background: 'var(--button-background)',
                                '&:hover': {
                                    color: 'var(--button-color-hover)',
                                    background: 'var(--button-background-hover)'
                                }
                            },
                            outlined: {
                                color: 'var(--button-outline-color)',
                                borderColor: 'var(--button-outline-color)',
                                background: 'var(--button-outline-background)',
                                '&:hover': {
                                    color: 'var(--button-outline-color-hover)',
                                    borderColor: 'var(--button-outline-color-hover)',
                                    background: 'var(--button-outline-background-hover)'
                                }
                            }
                        }
                    },
                    MuiChip: {
                        styleOverrides: {
                            root: {
                                fontSize: '0.8rem'
                            }
                        }
                    },
                    MuiDialogActions: {
                        styleOverrides: {
                            root: {
                                paddingTop: 'calc(2 * var(--mui-spacing))',
                                paddingLeft: 'calc(3 * var(--mui-spacing))',
                                paddingRight: 'calc(3 * var(--mui-spacing))',
                                paddingBottom: 'calc(2 * var(--mui-spacing))',
                                gap: 'calc(2 * var(--mui-spacing))',
                                // Prefer gap over MUI's default sibling margin.
                                '& > :not(style) ~ :not(style)': {
                                    marginLeft: 0
                                }
                            }
                        }
                    },
                    MuiDialogContent: {
                        styleOverrides: {
                            root: ({ theme }) => ({
                                wordBreak: 'break-word',
                                /** Very subtle inner shadow from the bottom (scroll affordance). */
                                boxShadow:
                                    theme.palette.mode === 'dark'
                                        ? 'inset 0 -10px 14px -6px rgba(0, 0, 0, 0.32)'
                                        : 'inset 0 -18px 24px -6px rgba(0, 0, 0, 0.05)'
                            })
                        }
                    },
                    MuiFormControl: {
                        styleOverrides: {
                            root: {
                                borderRadius: 'var(--field-radius, 4px)'
                            }
                        }
                    },
                    MuiFormControlLabel: {
                        styleOverrides: {
                            root: {
                                '& .MuiFormControlLabel-label': {
                                    userSelect: 'none'
                                }
                            }
                        }
                    },
                    MuiIconButton: {
                        styleOverrides: {
                            root: {
                                '&': {
                                    transition: 'transform 0.3s ease-in-out'
                                },
                                '&.flipped': {
                                    transform: 'rotate(-180deg)'
                                }
                            }
                        }
                    },
                    MuiList: {
                        styleOverrides: {
                            root: {
                                '&.topnav .MuiListItemText-primary': {
                                    fontFamily: 'var(--menu-font-family, "Inter")',
                                    fontSize: '0.9375rem',
                                    fontWeight: 'calc(var(--menu-weight, 400) + 100)',
                                    color: 'var(--menu-navigation, #000)',
                                    textTransform: 'var(--menu-text-case, none)'
                                },
                                '&.topnav .nav-heading': {
                                    fontFamily: 'var(--menu-font-family, "Inter")',
                                    fontSize: '0.6875rem',
                                    fontWeight: 'calc(var(--menu-weight, 400) + 300)',
                                    color: 'var(--menu-heading, #000)',
                                    textTransform: 'uppercase'
                                },
                                '&.topnav .MuiListItemIcon-root': {
                                    color: 'var(--menu-navigation, #000)',
                                    opacity: 0.8
                                },
                                '&.topnav .MuiIconButton-root ': {
                                    color: 'var(--menu-navigation, #000)',
                                    opacity: 0.8
                                },
                                '&.subnav': {
                                    paddingBottom: 12
                                },
                                '&.subnav .MuiListItem-root': {
                                    paddingTop: 2,
                                    paddingBottom: 2,
                                    paddingLeft: 24
                                },
                                '&.subnav .MuiListItemText-primary': {
                                    fontSize: '0.875rem',
                                    fontWeight: 'var(--menu-weight, 400)'
                                },
                                '&.topnav .MuiListItem-root.active': {
                                    backgroundColor:
                                        'var(--menu-background-selected, rgba(0, 0, 0, 0.04))'
                                },
                                '&.topnav .MuiListItem-root:hover': {
                                    backgroundColor:
                                        'var(--menu-background-hover, rgba(0, 0, 0, 0.04))'
                                },
                                '&.topnav .MuiListItem-root.active .MuiListItemIcon-root': {
                                    color: 'var(--menu-selected, #1565C0)'
                                },
                                '&.topnav .MuiListItem-root.active .MuiListItemText-primary': {
                                    color: 'var(--menu-selected, #1565C0)',
                                    fontWeight: 'calc(var(--menu-weight, 400) + 300) !important',
                                    fontfeaturesettings: "'liga' off, 'clig' off"
                                }
                            }
                        }
                    },
                    MuiListItem: {
                        styleOverrides: {
                            root: {
                                '&': {
                                    borderRadius: 'var(--button-radius, 4px)'
                                }
                            }
                        }
                    },
                    MuiListItemButton: {
                        styleOverrides: {
                            root: {
                                '&': {
                                    borderRadius: 'var(--button-radius, 4px)'
                                },
                                '&:hover': {
                                    backgroundColor:
                                        'var(--menu-background-hover, rgba(0, 0, 0, 0.04))'
                                }
                            }
                        }
                    },

                    MuiListItemIcon: {
                        styleOverrides: {
                            root: {
                                '&': {
                                    minWidth: 32
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: 'var(--font-size-125-rem, 20px)'
                                }
                            }
                        }
                    },
                    MuiListItemText: {
                        defaultProps: {
                            primaryTypographyProps: {
                                color: 'var(--primary-text, rgba(0, 0, 0, 0.87));',
                                fontFamily: 'var(--body-font-family, Inter)',
                                fontSize: 'var(--font-size-0875-rem, 14px)',
                                fontStyle: 'normal',
                                fontWeight: 'var(--fontWeightRegular, 400)',
                                lineHeight: '24px',
                                letterSpacing: '0.17px'
                            }
                        }
                    },
                    MuiMenuItem: {
                        styleOverrides: {
                            root: {
                                '&.selected': {
                                    background: 'rgba(0,0,0,0.04)',
                                    fontWeight: 'bold'
                                },
                                '&.x-selected': {
                                    position: 'relative'
                                },
                                '&.x-selected::before': {
                                    content: "''",
                                    position: 'absolute',
                                    top: 2,
                                    left: 0,
                                    bottom: 2,
                                    width: 6,
                                    background: 'var(--primary-main, #1565C0)'
                                }
                            }
                        }
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: {
                                borderRadius: 'var(--field-radius, 4px)',
                                background: 'var(--background-content, #fff)',
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor:
                                        'rgba(var(--mui-palette-common-onBackgroundChannel) / 0.5)'
                                }
                            }
                        }
                    },
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                borderRadius: 'var(--container-radius, 4px)',
                                border: '0.5px solid var(--field-outline-color, rgba(0, 0, 0, 0.12))',
                                '&.content': {
                                    background: 'var(--background-content, #FFFFFF)',
                                    padding: '20px 24px',
                                    marginBottom: '24px',
                                    position: 'relative',
                                    '--stripe-color': 'var(--primary-main)'
                                },
                                '&.content .header': {
                                    padding: '20px 24px',
                                    margin: '-20px -24px',
                                    userSelect: 'none'
                                },
                                '&.content.dense': {
                                    padding: '12px 16px 12px 16px'
                                },
                                '&.content.dense .header': {
                                    padding: '12px 16px 12px 16px',
                                    margin: '-12px -16px -12px -16px'
                                },
                                '&.content.dense .header button': {
                                    margin: '0 -8px 0 8px'
                                },
                                '&.content.stripe': {
                                    boxShadow: '3px 0px var(--stripe-color) inset'
                                },
                                '&.content h6': {
                                    fontSize: '1.25rem'
                                },
                                '&.content.admin-area': {
                                    background:
                                        'color-mix(in srgb, var(--primary-main, #1565C0) 7%, var(--background-content, #FFFFFF) 100%)'
                                }
                            }
                        }
                    },
                    MuiSelect: {
                        styleOverrides: {
                            root: ({ theme }) => ({
                                borderRadius: 'var(--field-radius, 4px)',
                                '&.TwoLine .MuiSelect-select': {
                                    minHeight: '2.8rem',
                                    height: 'auto',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    textOverflow: 'clip',
                                    lineHeight: 1.35,
                                    paddingTop: '10px',
                                    paddingBottom: '10px'
                                },
                                '&.TwoLine .MuiSelect-select::before': {
                                    content: "''",
                                    position: 'absolute',
                                    background: theme.palette.background.paper,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    height: '5px'
                                }
                            })
                        }
                    },
                    MuiTable: {
                        styleOverrides: {
                            root: {
                                background: 'var(--background-content, #FFFFFF)',
                                '&.no-lines .MuiTableCell-root': {
                                    border: '0 !important'
                                }
                            }
                        }
                    },
                    MuiTableHead: {
                        styleOverrides: {
                            root: {
                                '& .MuiTableCell-root': {
                                    background: 'var(--table-header-bg, rgba(0, 0, 0, 0.04))'
                                }
                            }
                        }
                    },
                    MuiTableCell: {
                        styleOverrides: {
                            root: {
                                '&': {
                                    wordBreak: 'break-word'
                                }
                            }
                        }
                    },
                    MuiTabs: {
                        styleOverrides: {
                            root: {
                                '& .MuiTab-root': {
                                    color: 'var(--primary-text, rgba(0, 0, 0, 0.87))',
                                    fontFamily: 'var(--body-font-family, Inter)'
                                }
                            }
                        }
                    },
                    MuiTextField: {
                        styleOverrides: {
                            root: {
                                '& input': {
                                    color: 'var(--primary-text, rgba(0, 0, 0, 0.87))',
                                    fontfeaturesettings: "'liga' off, 'clig' off",
                                    fontFamily: 'var(--body-font-family, Inter)',
                                    fontSize: 'var(--_fontSize-1rem, 16px)',
                                    fontStyle: 'normal',
                                    fontWeight: 'var(--fontWeightRegular, 400)',
                                    lineHeight: '24px',
                                    letterSpacing: '0.15px'
                                }
                            }
                        }
                    },
                    /*
                     * ★ TOOLTIP TEXT IS 13px, REGULAR WEIGHT, PLATFORM-WIDE (user decision
                     * 2026-09-14). MUI's default is 11px at medium weight (`pxToRem(11)`,
                     * `fontWeightMedium`) — well under the 14px body text, and hard to read once a
                     * tooltip carries a sentence. MUI's long-press touch variant (14px, regular) gets
                     * the same 13px, so a tooltip reads the same on every device. Pinned by
                     * `__tests__/theme-provider-tooltip.test.tsx`.
                     */
                    MuiTooltip: {
                        styleOverrides: {
                            tooltip: {
                                fontSize: '0.8125rem',
                                fontWeight: 400
                            }
                        }
                    },
                    MuiToggleButtonGroup: {
                        styleOverrides: {
                            root: {
                                background: 'var(--button-outline-background, #FFFFFF)',
                                borderRadius: 'var(--button-radius, 4px)',
                                '--mui-shape-borderRadius': 'var(--button-radius, 4px)'
                            }
                        }
                    },
                    MuiToggleButton: {
                        styleOverrides: {
                            root: {
                                borderRadius: 'var(--button-radius, 4px)',
                                fontFamily: 'var(--button-font-family, "Inter")',
                                fontWeight: 'var(--button-weight, 400)',
                                borderWidth: 1,
                                color: 'var(--button-outline-color, var(--primary-main, #1565C0))',
                                borderColor:
                                    'var(--button-outline-color, var(--primary-main, #1565C0))',
                                background: 'var(--button-outline-background, transparent)',
                                '&:hover': {
                                    color: 'var(--button-outline-color-hover, var(--primary-main, #1565C0))',
                                    borderColor:
                                        'var(--button-outline-color-hover, var(--primary-main, #1565C0))',
                                    background:
                                        'var(--button-outline-background-hover, transparent)'
                                },
                                '&.Mui-selected': {
                                    color: 'var(--button-color, #ffffff)',
                                    background:
                                        'var(--button-background, var(--primary-main, #1565C0))',
                                    borderColor:
                                        'var(--button-background, var(--primary-main, #1565C0))',
                                    '&:hover': {
                                        color: 'var(--button-color-hover, #ffffff)',
                                        background:
                                            'var(--button-background-hover, var(--primary-main-darker, #11519a))',
                                        borderColor:
                                            'var(--button-background-hover, var(--primary-main-darker, #11519a))'
                                    }
                                }
                            }
                        }
                    }
                },
                typography: {
                    h1: {
                        color: 'var(--headline-color, #303030)',
                        fontFamily: 'var(--headline-font-family, Inter)',
                        fontSize: '1.25rem',
                        fontWeight: 'var(--headline-weight, 700)'
                    },
                    h2: {
                        color: 'var(--headline-color, #303030)',
                        fontFamily: 'var(--headline-font-family, Inter)',
                        fontSize: '1.125rem',
                        fontWeight: 'var(--headline-weight, 700)'
                    },
                    h3: {
                        color: 'var(--headline-color, #303030)',
                        fontFamily: 'var(--headline-font-family, Inter)',
                        fontSize: '1rem',
                        fontWeight: 'var(--headline-weight, 700)'
                    },
                    h4: {
                        color: 'var(--headline-color, #303030)',
                        fontFamily: 'var(--headline-font-family, Inter)',
                        fontSize: '0.9rem',
                        fontWeight: 'var(--headline-weight, 700)'
                    },
                    h5: {
                        color: 'var(--headline-color, #303030)',
                        fontFamily: 'var(--headline-font-family, Inter)',
                        fontSize: '0.8rem',
                        fontWeight: 'var(--headline-weight, 700)'
                    },
                    h6: {
                        color: 'var(--headline-color, #303030)',
                        fontFamily: 'var(--headline-font-family, Inter)',
                        fontSize: '0.7rem',
                        fontWeight: 'var(--headline-weight, 700)'
                    },
                    body1: {
                        color: 'var(--body-color, #000)',
                        fontFamily: 'var(--body-font-family, Inter)',
                        fontWeight: 'var(--body-weight, 400)'
                    },
                    body2: {
                        color: 'var(--body-color, #000)',
                        fontFamily: 'var(--body-font-family, Inter)',
                        fontWeight: 'var(--body-weight, 400)'
                    },
                    caption: {
                        color: 'var(--body-color, #000)',
                        fontFamily: 'var(--body-font-family, Inter)',
                        fontWeight: 'var(--body-weight, 400)'
                    }
                }
            }),
        [useDark]
    );

    return (
        <MuiThemeProvider theme={appTheme}>
            <CssBaseline />
            {children}
        </MuiThemeProvider>
    );
}
